<script lang="ts" setup>
import CompletedProgress from '@/vue/atoms/completed-progress.vue';
import BackLink from '@/vue/molecules/back-link.vue';
import type {RouteLocationRaw} from 'vue-router';
import {useScreenSize} from '@/ts/composables/stateful/use-screen-size';
import {computed, ref} from 'vue';
import {TableOfContentItemState, type TableOfContentItem} from '@/ts/types/component/table-of-content.type';
import TableOfContentItemUi from '@/vue/molecules/table-of-content-item-ui.vue';

const props = defineProps<{
  backLink: RouteLocationRaw;
  backLinkLabel: string;
  title: string;
  completedPercent: number;
  neverScaleDown?: boolean;
  items: TableOfContentItem[];
  itemCounterLabelKey: string;
}>();

const nav = ref<HTMLElement>();
const tocItems = ref();
const boundaryInlineStyle = ref<string>('');
const {isMobileResolution} = useScreenSize();
const isMenuScaledDownUnlockedByUser = ref<boolean>(false);
const isMenuScaledDown = computed<boolean>(() => {
  return !props.neverScaleDown &&
      isMobileResolution.value &&
      !isMenuScaledDownUnlockedByUser.value &&
      props.items.length > 3;
});

const navId = `session-navigation-${ crypto.randomUUID() }`;

function onClickShowAllMenuItems(): void {
  isMenuScaledDownUnlockedByUser.value = true;

  // focus first nav item (needed for keyboard control)
  if (tocItems.value?.[0]) { //eslint-disable-line
    tocItems.value[0].focus(); //eslint-disable-line
  }

  // ensure transition animation
  boundaryInlineStyle.value =
      nav.value?.offsetHeight ?
          `height: ${nav.value?.offsetHeight}px; overflow: hidden;`
          : 'auto';
  window.setTimeout(() => {
    boundaryInlineStyle.value = '';
  }, 500);
}

function onClickShowLessMenuItems(): void {
  isMenuScaledDownUnlockedByUser.value = false;
  // Maybe focus the show all button again? Requires ref on that button.
}

const displayedTocItems = computed<TableOfContentItem[]>(() => {
  const allPages = props.items;
  const currentPageIndex = allPages.findIndex((item) => item.state === TableOfContentItemState.Current);
  const totalPages = allPages.length;

  if (!isMenuScaledDown.value || totalPages < 4) {
    // Show all pages if expanded or less than 4 pages total
    return allPages;
  }

  let pagesToShow: TableOfContentItem[] = [];

  if (currentPageIndex < 1) {
    // First page active: show 1 (current), 2 (neutral), 3 (faded)
    pagesToShow = [
      {...allPages[0], isFaded: false},
      {...allPages[1], isFaded: false},
      {...allPages[2], isFaded: true},
    ] as TableOfContentItem[];
  } else if (currentPageIndex === totalPages - 1) {
    // Last page active: show N-2 (complete, faded), N-1 (complete), N (current)
    pagesToShow = [
      {...allPages[totalPages - 3], isFaded: true},
      {...allPages[totalPages - 2], isFaded: false},
      {...allPages[totalPages - 1], isFaded: false},
    ] as TableOfContentItem[];
  } else {
    // Middle page active: show N-1 (complete), N (current), N+1 (faded)
    pagesToShow = [
      {...allPages[currentPageIndex - 1], isFaded: false},
      {...allPages[currentPageIndex], isFaded: false},
      {...allPages[currentPageIndex + 1], isFaded: true},
    ] as TableOfContentItem[];
  }

  return pagesToShow;
});

</script>

<template>
  <div ref="wrapper">
    <back-link :label="backLinkLabel" :to="backLink" :hide-arrow="true"/>
    <div class="text-body-sm-bold">
      {{ title }}
    </div>
    <completed-progress :completed="completedPercent"/>
    <div
      v-if="displayedTocItems.length > 1"
      class="nav-boundary"
      :class="{'nav-boundary--scaled-down': isMenuScaledDown}"
      :style="boundaryInlineStyle"
    >
      <nav :id="navId" ref="nav" :aria-label="$t('session_nav')">
        <ol ref="navList" class="nav-session-pages">
          <table-of-content-item-ui
            v-for="item in displayedTocItems"
            ref="tocItems"
            :key="item.id"
            :index="item.index"
            :is-inactive="isMenuScaledDown"
            :style="item.isFaded ? {opacity: 0.5} : {}"
            :state="item.state"
            :title="item.title"
            :link="item.link"
            :item-counter-label-key="itemCounterLabelKey"
          />
        </ol>
      </nav>
      <q-btn
        v-if="isMenuScaledDown"
        class="btn-show-all"
        :aria-expanded="!isMenuScaledDown"
        :aria-controls="navId"
        :aria-label="$t('session_nav_expand_btn_aria')"
        flat
        dense
        @click="onClickShowAllMenuItems"
      >
        <span class="text-body-sm-bold">{{ $t('session_nav_expand_btn') }}</span>
      </q-btn>
      <q-btn
        v-if="!isMenuScaledDown && isMenuScaledDownUnlockedByUser && isMobileResolution"
        class="btn-show-less"
        :aria-expanded="!isMenuScaledDown"
        :aria-controls="navId"
        :aria-label="$t('session_nav_collapse_btn_aria')"
        flat
        dense
        @click="onClickShowLessMenuItems"
      >
        <span class="text-body-sm-bold">{{ $t('session_nav_collapse_btn') }}</span>
      </q-btn>
    </div>
  </div>
</template>
<style scoped lang="scss">
.nav-session-pages {
  @include dashed-between-elements(var(--Spacing-spacing-2));
  width: 100%;
  margin: 0;
  padding: 0;
}

.nav-boundary {
  // has inline-style set by JavaScript in order to set height for the transition
  transition: height .3s linear;
  margin: var(--Spacing-spacing-5) -5px 0 -5px;
  padding: 5px; // give room for outline of :focus-visible (keyboard usage)

  &--scaled-down {
    position: relative;

    @include screen-lg {
      // max-height: none;
      // &:after {
      //   content: none;
      // }
    }
  }
}

.btn-show-all {
  @include focus-visible;
  // position: absolute;
  // bottom: 10px;
  // z-index: 1;
  // left: 50%;
  // transform: translate(-50%, 0);

  // Center button horizontally and add space above
  display: block;
  margin: var(--Spacing-spacing-4) auto var(--Spacing-spacing-4) auto;

  @include screen-lg {
    display: none;
  }
}

// Added style for show less button (can reuse show-all)
.btn-show-less {
  @include focus-visible;
  display: block;
  margin: var(--Spacing-spacing-4) auto var(--Spacing-spacing-4) auto;

  @include screen-lg {
    display: none;
  }
}
</style>
